import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import type { DataTableExpandedRows, DataTableValueArray } from 'primereact/datatable'
import { getApiErrorCode } from '../../api/apiError'
import Button from '../../components/ui/Button'
import { rowActionLabel } from '../../lib/deleteLabel'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import GroupFormDialog from './components/GroupFormDialog'
import GroupMembersPanel from './components/GroupMembersPanel'
import { useGroups } from './hooks/useGroups'
import { useProxyMode } from '../proxy/useProxyMode'
import type { Group } from './types'
import './admin.css'

/**
 * Admin → <b>Grup Yönetimi</b> (122). Kullanıcı grupları kataloğu ve üyelikleri — DVA'daki ekranın
 * karşılığı. Grup <b>eklenmez, silinmez, pasife alınmaz</b>; yalnız adı (TR/EN) düzenlenir. Bir satır
 * açılınca gruptaki kullanıcılar listelenir; buradan gruba kullanıcı eklenir ve gruptan çıkarılır.
 * Kullanıcının gruplarını kullanıcı tarafından yönetmek Kullanıcı Yönetimi'ndedir ("Gruplar").
 */
export default function GroupsAdminPage() {
  const { t, i18n } = useTranslation()
  const en = i18n.language.startsWith('en')
  const toast = useToast()
  // Kısıtlı Yetkili asistan (126): ad düzenleme ve üye ekleme/çıkarma çizilmez; liste ve üyeler okunur.
  const { readOnly } = useProxyMode()
  const { list, update, addMember, removeMember } = useGroups()
  const rows = list.data ?? []

  const [editing, setEditing] = useState<Group | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined)
  const clearNameError = useCallback(() => setNameError(null), [])

  const onSave = (values: { nameTr: string; nameEn: string | null }) => {
    if (!editing) return
    update.mutate(
      { id: editing.id, body: { ...values, modDate: editing.modDate } },
      {
        onSuccess: () => {
          toast.show({ severity: 'success', summary: t('admin.groups.toast.saved'), life: 3000 })
          setEditing(null)
        },
        onError: (err) => {
          if (getApiErrorCode(err) === 'DUPLICATE_NAME') setNameError(t('admin.groups.errors.duplicateName'))
        },
      },
    )
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <GroupFormDialog
        visible={editing != null}
        group={editing}
        submitting={update.isPending}
        nameError={nameError}
        onClearNameError={clearNameError}
        onSubmit={onSave}
        onHide={() => setEditing(null)}
      />

      <PageHeader title={t('nav.admin.groups')} actions={<RefreshButton onRefresh={() => list.refetch()} />} />

      <DataTable
        value={rows}
        loading={list.isLoading}
        dataKey="id"
        paginator={false}
        expandedRows={expanded}
        onRowToggle={(e) => setExpanded(e.data)}
        rowExpansionTemplate={(g: Group) => (
          <GroupMembersPanel
            group={g}
            readOnly={readOnly}
            adding={addMember.isPending}
            removing={removeMember.isPending}
            onAdd={(userId, onDone) => addMember.mutate({ groupId: g.id, userId }, { onSuccess: onDone })}
            onRemove={(m) =>
              removeMember.mutate(
                { groupId: g.id, userId: m.userId },
                {
                  onSuccess: () =>
                    toast.show({ severity: 'success', summary: t('admin.groups.members.removed'), life: 3000 }),
                },
              )
            }
          />
        )}
        emptyMessage={<EmptyState title={t('admin.groups.empty')} />}
      >
        {/* Dar ok sütunu: hücre iç boşluğu ve düğme boyu küçültülür (admin.css .admin-expander-col). */}
        <Column
          expander
          headerClassName="admin-expander-col"
          bodyClassName="admin-expander-col"
          style={{ width: '2.25rem' }}
        />
        {/* Tek "Grup Adı" kolonu, seçili dile göre (kullanıcı isteği 09.10.2026); EN boşsa TR'ye düşer —
            Kullanıcı Yönetimi'ndeki rol/grup adlarıyla aynı kural. İki dil birlikte düzenleme pop-up'ında. */}
        <Column
          header={t('admin.groups.columns.name')}
          body={(g: Group) => (en && g.nameEn ? g.nameEn : g.nameTr)}
        />
        <Column field="memberCount" header={t('admin.groups.columns.memberCount')} style={{ width: '9rem' }} />
        {/* Tek eylemi (Düzenle) salt okunur kipte kalkan kolon bütünüyle çizilmez. */}
        {!readOnly && (
          <Column
            header={t('admin.groups.columns.actions')}
            style={{ width: '6rem' }}
            body={(g: Group) => {
              // Erişilebilir ad satırın grubunu TAŞIR: aynı sütundaki her düğme sabit "Düzenle"
              // deseydi ekran okuyucu ve sesli kontrol kullanıcısı satırları ayırt edemezdi
              // (sesli kontrolde ad, düğmenin tek tanımlayıcısıdır). Ad kolonunun gövdesiyle AYNI
              // ifade kullanılır, yoksa duyulan ad ile görünen ad ayrışır.
              const name = en && g.nameEn ? g.nameEn : g.nameTr
              // `title` de aynı değeri alır: deleteLabel javadoc'u ikisinin ayrışmamasını şart koşuyor
              // ve depodaki üç çağrı yeri de öyle yapıyor.
              const label = rowActionLabel(t, 'admin.groups.actions.edit', name)
              return (
              <Button
                icon="pi pi-pencil"
                rounded
                text
                aria-label={label}
                tooltip={label}
                // Kolon tablonun en sağında: varsayılan (sağa açılan) tooltip ekran dışına taşıyordu.
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => setEditing(g)}
              />
              )
            }}
          />
        )}
      </DataTable>
    </div>
  )
}
